/**
 * CSV / TSV dialect model (Requirement 10.3).
 *
 * A dialect is the complete set of choices a user makes in the import or export
 * dialog: which delimiter separates fields, which encoding the bytes are in,
 * whether the first record is a header, and which newline the writer emits.
 * Both the parser and the writer take the same record, which is what keeps the
 * round trip of Requirement 10.11 honest — there is no hidden second opinion
 * about what a comma means.
 *
 * The quote character is fixed at `"`. Requirement 10.3 only asks for escaped
 * double-quote characters, and a configurable quote would multiply the
 * state-machine cases without buying anything the requirements ask for.
 */

/** Encodings the import dialog can name. */
export type CsvEncoding = 'utf-8' | 'utf-16le' | 'windows-1252';

/** The dialect record shared by {@link CsvStreamParser} and `writeCsv`. */
export interface CsvDialect {
  /** Single-character field separator. */
  delimiter: string;
  /** Fixed: the only quote character the codec recognises. */
  quote: '"';
  /** Encoding of the *bytes* fed to the parser. Irrelevant to text-mode use. */
  encoding: CsvEncoding;
  /** Whether the first record names the columns. */
  hasHeader: boolean;
  /** Record terminator the writer emits. The parser accepts all three. */
  newline: '\n' | '\r\n';
}

/** Delimiters offered in the UI, in sniffing preference order. */
export const SUPPORTED_DELIMITERS: readonly string[] = [',', '\t', ';', '|'];

/** Encodings the byte-mode parser can decode. */
export const SUPPORTED_ENCODINGS: readonly CsvEncoding[] = ['utf-8', 'utf-16le', 'windows-1252'];

/** Comma-separated, UTF-8, header row, LF terminators. */
export const DEFAULT_CSV_DIALECT: CsvDialect = {
  delimiter: ',',
  quote: '"',
  encoding: 'utf-8',
  hasHeader: true,
  newline: '\n',
};

/** Tab-separated variant of {@link DEFAULT_CSV_DIALECT}. */
export const DEFAULT_TSV_DIALECT: CsvDialect = { ...DEFAULT_CSV_DIALECT, delimiter: '\t' };

/** A dialect field that cannot be honoured. */
export class CsvDialectError extends Error {
  override readonly name = 'CsvDialectError';
  /** The offending field. */
  readonly field: keyof CsvDialect;

  constructor(field: keyof CsvDialect, reason: string) {
    super(`invalid CSV dialect field ${field}: ${reason}`);
    this.field = field;
  }
}

/**
 * Fills in the defaults and rejects a delimiter the state machine could not
 * distinguish from its own control characters. Validation happens here, once,
 * so neither the parser nor the writer has to re-check.
 */
export function resolveDialect(partial: Partial<CsvDialect> = {}): CsvDialect {
  const dialect: CsvDialect = { ...DEFAULT_CSV_DIALECT, ...partial, quote: '"' };
  const d = dialect.delimiter;
  if (d.length !== 1) {
    throw new CsvDialectError('delimiter', `expected one character, got ${JSON.stringify(d)}`);
  }
  if (d === '"' || d === '\r' || d === '\n') {
    throw new CsvDialectError('delimiter', `${JSON.stringify(d)} is reserved`);
  }
  if (!SUPPORTED_ENCODINGS.includes(dialect.encoding)) {
    throw new CsvDialectError('encoding', `unsupported encoding ${JSON.stringify(dialect.encoding)}`);
  }
  if (dialect.newline !== '\n' && dialect.newline !== '\r\n') {
    throw new CsvDialectError('newline', 'expected "\\n" or "\\r\\n"');
  }
  return dialect;
}

/**
 * Dialect implied by a file name: `.tsv` and `.tab` are tab-separated,
 * everything else is comma-separated. The Import_Service (task 20) uses this as
 * the initial state of the import dialog, which the user can override.
 */
export function dialectForFileName(name: string, partial: Partial<CsvDialect> = {}): CsvDialect {
  const lower = name.toLowerCase();
  const tabbed = lower.endsWith('.tsv') || lower.endsWith('.tab');
  return resolveDialect({ delimiter: tabbed ? '\t' : ',', ...partial });
}
